import React, {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';
import { TextComposer } from 'stream-chat';

type TextAreaRefType = MutableRefObject<HTMLTextAreaElement | null | undefined>;

interface AssistantContextType {
  setTextareaRef: Dispatch<SetStateAction<TextAreaRefType | null>>;
  setTextComposer: Dispatch<SetStateAction<TextComposer | null>>;
  setMessageDraft: (message: string) => void;
  isSetMessageDraftReady: boolean;
  extensionTabKey: string;
  incrementExtensionTabKey: () => void;
}

const AssistantContext = createContext<AssistantContextType | undefined>(
  undefined,
);

interface AssistantProviderProps {
  children: ReactNode;
}

export const AssistantProvider: React.FC<AssistantProviderProps> = ({
  children,
}) => {
  const [textareaRef, setTextareaRef] = useState<TextAreaRefType | null>(null);
  const [textComposer, setTextComposer] = useState<TextComposer | null>(null);

  const setMessageDraft = useCallback(
    (message: string) => {
      if (textareaRef && textareaRef.current && textComposer) {
        textareaRef.current.focus();
        void textComposer
          .handleChange({
            text: message,
            selection: {
              start: message.length,
              end: message.length,
            },
          })
          .then(() => {
            // Scroll to bottom of textarea after text is set
            if (textareaRef.current) {
              textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
            }
          });
      }
    },
    [textComposer, textareaRef],
  );

  const [extensionTabKeyCount, setExtensionTabKeyCount] = useState(1);
  const incrementExtensionTabKey = useCallback(() => {
    setExtensionTabKeyCount((prev) => prev + 1);
  }, []);

  return (
    <AssistantContext.Provider
      value={{
        setTextareaRef,
        setTextComposer,
        setMessageDraft,
        isSetMessageDraftReady: textareaRef !== null && textComposer !== null,
        extensionTabKey: `extensionTab-${extensionTabKeyCount}`,
        incrementExtensionTabKey,
      }}
    >
      {children}
    </AssistantContext.Provider>
  );
};

export const useAssistantContext = (): AssistantContextType => {
  const context = useContext(AssistantContext);
  if (context === undefined) {
    throw new Error(
      'useAssistantContext must be used within an AssistantProvider',
    );
  }
  return context;
};
